import axios from 'axios';
import { HERE_MAP_API_KEY } from '../constants';
import { Nullable } from '../types';
import { mphToKph } from './locale';

interface SpeedLimitAttribute {
  FROM_REF_SPEED_LIMIT?: string;
  TO_REF_SPEED_LIMIT?: string;
  SPEED_LIMIT_SOURCE?: string;
  SPEED_LIMIT_UNIT?: string;
}

interface RouteMatchingResponse {
  response?: {
    route?: {
      leg?: {
        link?: {
          linkId?: string;
          attributes?: {
            SPEED_LIMITS_FCN?: SpeedLimitAttribute[];
          };
        }[];
      }[];
    }[];
  };
}

export type LatLngWaypoint = [number, number];

const toWaypointParam = ([lat, lng]: LatLngWaypoint, heading?: number): string => {
  return heading === undefined ? `${lat},${lng}` : `${lat},${lng};;;${heading}`;
};

export const getSpeedLimit = async (waypoints: LatLngWaypoint[], heading?: number, mode = 'car', apiKey?: string): Promise<Nullable<number>> => {
  const url = 'https://routematching.hereapi.com/v8/calculateroute.json';
  const waypointParams = waypoints.reduce<Record<string, string>>((acc, waypoint, index) => {
    acc[`waypoint${index}`] = toWaypointParam(waypoint, index === 0 ? heading : undefined);
    return acc;
  }, {});

  const response = await axios.get<RouteMatchingResponse>(url, {
    params: {
      ...waypointParams,
      apikey: apiKey ?? HERE_MAP_API_KEY,
      routeMatch: 1,
      mode: `fastest;${mode};traffic:disabled`,
      attributes: 'SPEED_LIMITS_FCn(FROM_REF_SPEED_LIMIT,TO_REF_SPEED_LIMIT,SPEED_LIMIT_UNIT)',
    },
  });

  const link = response.data.response?.route?.[0]?.leg?.[0]?.link?.[0];
  const attribute = link?.attributes?.SPEED_LIMITS_FCN?.[0];
  const drivenAgainstReference = link?.linkId?.startsWith('-') ?? false;
  const rawSpeedLimit = drivenAgainstReference ? attribute?.TO_REF_SPEED_LIMIT : attribute?.FROM_REF_SPEED_LIMIT;
  const speedLimit = rawSpeedLimit ? parseInt(rawSpeedLimit, 10) : NaN;

  if (!speedLimit || isNaN(speedLimit)) return null;

  return attribute?.SPEED_LIMIT_UNIT === 'M' ? mphToKph(speedLimit) : speedLimit;
};
